'use client';

import type { ReactNode } from 'react';

import { AppIonPage } from '@/core/components/app-ion-page';
import { AppPageContent } from '@/core/components/app-page-content';
import { AppPageLayout } from '@/core/components/app-page-layout';
import { FeedbackFeatureGate } from '@/core/components/feedback/feedback-feature-gate';
import { PageHeader } from '@/core/components/page-header';
import { useFeedbackFeatureEnabled } from '@/core/hooks/use-feedback';
import { useInvalidateOnIonViewEnter } from '@/core/hooks/use-invalidate-on-ion-view-enter';

type FeedbackPageShellProps = {
  title: string;
  description?: string;
  breadcrumb?: ReactNode;
  actions?: ReactNode;
  actionsLayout?: 'inline' | 'stack';
  children: ReactNode;
};

/**
 * Shared chrome for Feedback list/hub pages — header + entitlement gate.
 * Header actions are hidden while the plan lacks Feedback.
 * Section switching lives in the sidebar / More hub (and People-style
 * inner tabs on Activity / Library), not a top inter-page tab strip.
 */
export function FeedbackPageShell({
  title,
  description,
  breadcrumb,
  actions,
  actionsLayout,
  children,
}: FeedbackPageShellProps) {
  useInvalidateOnIonViewEnter([['feedback']]);
  const feedbackEnabled = useFeedbackFeatureEnabled();

  return (
    <AppIonPage>
      <AppPageContent>
        <AppPageLayout>
          <PageHeader
            title={title}
            description={description}
            breadcrumb={breadcrumb}
            // Locked plan: the teaser card is the only action (DEV-549).
            actions={feedbackEnabled ? actions : undefined}
            actionsLayout={actionsLayout}
          />
          <FeedbackFeatureGate>{children}</FeedbackFeatureGate>
        </AppPageLayout>
      </AppPageContent>
    </AppIonPage>
  );
}
